<script setup lang="ts">
import { ref, watch, reactive, onMounted, inject } from "vue";
import type { Ref } from "vue";
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from "aws-amplify/auth";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import { requiredRule } from "@/utilities/requiredRule";
import { useRouter, useRoute } from "vue-router";
import { isValidRedirectUrl } from "@/utilities/isValidRedirectUrl";
import type { RetailInstanceTypeService } from "@/services/RetailInstanceTypeService";
import { retailInstanceTypeSymbol } from "@/models/Symbols";

const router = useRouter();
const route = useRoute();
const form: Ref<boolean> = ref(false);
const email: Ref<string> = ref("");
const password: Ref<string> = ref("");
const loading: Ref<boolean> = ref(false);
const make_password_visible: Ref<boolean> = ref(false);
const current_step: Ref<string> = ref("login");
const reset_password = ref({
    password: "",
    confirm_password: "",
    make_password_visible: false,
    make_password_visible_second: false,
    error_message: "",
});
const error_message: Ref<string> = ref("");
const passwords_do_not_match_text: Ref<string> = ref("Passwords do not match.");
const forgot_password = reactive({
    email_address: "",
    verification_code: "",
    new_password: "",
    confirm_new_password: "",
});
const forgot_password_enter_code_form_valid: Ref<boolean> = ref(false);
const forgot_password_title: Ref<string> = ref("Forgot Password");
const redirectTo = ref("");
const instanceTypeSvc = inject(retailInstanceTypeSymbol) as RetailInstanceTypeService;

function onSubmit() {
    error_message.value = "";

    if (!form.value) return;

    loading.value = true;

    signIn({ username: email.value, password: password.value })
        .then((response) => {
            if (response.isSignedIn === false) {
                if (response.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    current_step.value = "new_password_required";
                }
            } else if (redirectTo.value && isValidRedirectUrl(redirectTo.value as string)) {
                router.push(redirectTo.value as string);
            } else {
                router.push({ name: "home" });
            }

            loading.value = false;
        })
        .catch((error) => {
            console.error(error);
            error_message.value = error.message;
            loading.value = false;
        });
}

// Submits the new password to confirm the sign in.
function onResetPasswordSubmit() {
    // Check both passwords match
    if (reset_password.value.password !== reset_password.value.confirm_password) {
        reset_password.value.error_message = passwords_do_not_match_text.value;
        return;
    }

    confirmSignIn({
        challengeResponse: reset_password.value.password,
    })
        .then(() => {
            router.push({ name: "bookings" });
        })
        .catch((response) => {
            if (_get(response, "message", false)) {
                reset_password.value.error_message = response.message;

                if (reset_password.value.error_message === "Invalid session for the user, session is expired.") {
                    reset_password.value.error_message = `${reset_password.value.error_message} Please refresh the page and try again.`;
                }
            } else {
                reset_password.value.error_message = "An unknown error occured. Please try again.";
            }
        });
}

// Starts the process to reset a forgotten password by executing Amplifys resetPassword()
function onForgotPasswordStartSubmit() {
    forgot_password.email_address = forgot_password.email_address.trim();

    resetPassword({ username: forgot_password.email_address })
        .then(() => {
            current_step.value = "forgot_password_enter_code";
        })
        .catch((error) => {
            console.error(error);
            // this.toast.error("An unknown error occured. Please try again.", { timeout: 10000 })
        });
}

// Submits the code and new password that the user entered to Amplify's confirmResetPassword()
function onForgotPasswordResetCodeSubmit() {
    forgot_password.verification_code = forgot_password.verification_code.trim();

    // Check both passwords match
    if (forgot_password.new_password !== forgot_password.confirm_new_password) {
        // this.toast.error("Passwords do not match.", { timeout: 10000 })
        return;
    }

    confirmResetPassword({
        confirmationCode: forgot_password.verification_code,
        newPassword: forgot_password.new_password,
        username: forgot_password.email_address,
    })
        .then(() => {
            current_step.value = "successfully_reset_password";
        })
        .catch((error) => {
            console.error(error);
            if (error.name === "CodeMismatchException") {
                error_message.value = "The code you entered is incorrect.";
            } else if (error.name === "InvalidPasswordException") {
                error_message.value = error.message;
            } else {
                error_message.value = "An unknown error occured. Please try again.";
            }
        });
}

function getRegisterUserLink() {
    if (instanceTypeSvc.isCorporate()) {
        return "/corporate/signup/";
    }
    return "/register/";
}

watch(forgot_password, async () => {
    if (_isEmpty(forgot_password.confirm_new_password)) return;

    if (forgot_password.new_password !== forgot_password.confirm_new_password) {
        error_message.value = passwords_do_not_match_text.value;
    } else if (error_message.value === passwords_do_not_match_text.value) {
        error_message.value = "";
    }
});

onMounted(() => {
    const { query } = router.currentRoute.value;

    if (query.reset) {
        current_step.value = "start_forgot_password";
        forgot_password_title.value = "Reset Password";
    }

    if (query.email) {
        forgot_password.email_address = query.email;
    }

    // Decode and store the RedirectTo URL if it exists in the query params
    const encodedRedirectTo = route.query.redirectTo;
    if (encodedRedirectTo) {
        redirectTo.value = decodeURIComponent(encodedRedirectTo as string);
    }
});
</script>

<template>
    <div class="login_container" v-if="current_step === 'login'">
        <v-card class="mx-auto px-6 py-8" elevation="2">
            <v-form v-model="form" @submit.prevent="onSubmit">
                <v-text-field v-model="email" :readonly="loading" :rules="[requiredRule]" class="mb-2" label="Email Address"></v-text-field>

                <v-text-field v-model="password" :readonly="loading" :rules="[requiredRule]" label="Password" placeholder="Enter your password" :type="make_password_visible ? 'text' : 'password'" @click:append-inner="make_password_visible = !make_password_visible" :append-inner-icon="make_password_visible ? 'mdi-eye' : 'mdi-eye-off'"></v-text-field>

                <br />

                <v-btn :disabled="!form" :loading="loading" color="var(--vt-c-orange)" size="large" type="submit" variant="elevated" block class="text-white"> Sign In </v-btn>

                <v-alert icon="mdi-alert-circle" :text="error_message" type="error" v-if="!_isEmpty(error_message)" class="mt-7" style="font-size: 1rem"></v-alert>

                <div class="d-flex justify-space-between align-center mt-5">
                    <span class="forgot_password_link" @click="current_step = 'start_forgot_password'">Forgot password</span>

                    <span class="register_link"><a :href="getRegisterUserLink()">Register</a></span>
                </div>
            </v-form>
        </v-card>
    </div>

    <div class="new_password_required" v-else-if="current_step === 'new_password_required'">
        <p class="mt-3 mb-5 mx-10">Please reset your password to continue.</p>

        <v-text-field v-model="reset_password.password" :rules="[requiredRule]" class="mb-2" label="New Password" placeholder="Enter your new password" :type="reset_password.make_password_visible ? 'text' : 'password'" @click:append-inner="reset_password.make_password_visible = !reset_password.make_password_visible" :append-inner-icon="reset_password.make_password_visible ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>

        <v-text-field v-model="reset_password.confirm_password" class="mb-2" :rules="[requiredRule]" label="Confirm password" placeholder="Confirm your new password" :type="reset_password.make_password_visible_second ? 'text' : 'password'" @click:append-inner="reset_password.make_password_visible_second = !reset_password.make_password_visible_second" :append-inner-icon="reset_password.make_password_visible_second ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>

        <v-alert class="mb-4">
            Password must have at least:
            <ul>
                <li>1 uppercase letter</li>
                <li>1 lowercase letter</li>
                <li>1 number</li>
                <li>1 special character</li>
                <li>8 characters in length</li>
            </ul>
        </v-alert>

        <v-alert style="max-width: 380px" type="error" v-if="!_isEmpty(reset_password.error_message)" class="mb-4" icon="mdi-alert-circle">
            <span>{{ reset_password.error_message }}</span>
        </v-alert>

        <v-btn color="var(--vt-c-orange)" size="large" variant="elevated" block class="text-white" @click="onResetPasswordSubmit()" :disabled="_isEmpty(reset_password.password.trim()) || _isEmpty(reset_password.confirm_password.trim())"> Change Password </v-btn>
    </div>

    <div class="forgot_password" v-else-if="current_step === 'start_forgot_password'">
        <v-card class="mx-auto px-6 py-8" elevation="2">
            <p class="mb-3 mx-10" style="font-weight: 500; font-size: 1.2rem; text-align: center">
                {{ forgot_password_title }}
            </p>
            <p class="mt-3 mb-6 mx-5">Enter your email address to receive a verification code.</p>

            <v-text-field v-model="forgot_password.email_address" :rules="[requiredRule]" label="Email Address" placeholder="Enter your email address" variant="outlined" class="mb-2"></v-text-field>

            <v-btn color="var(--vt-c-orange)" size="large" variant="elevated" block class="text-white" @click="onForgotPasswordStartSubmit()" :disabled="_isEmpty(forgot_password.email_address.trim())"> Send code </v-btn>
        </v-card>
    </div>

    <div class="forgot_password" v-else-if="current_step === 'forgot_password_enter_code'">
        <v-card class="mx-auto px-6 py-8" elevation="2">
            <p class="mb-3 mx-10" style="font-weight: 500; font-size: 1.2rem; text-align: center">Check your email</p>
            <p class="mt-3 mb-5 mx-5" style="text-align: center">
                Please enter the verification code that has<br />
                been sent to the email address.
            </p>

            <v-form v-model="forgot_password_enter_code_form_valid">
                <v-text-field v-model="forgot_password.verification_code" :rules="[requiredRule]" label="Reset code" placeholder="Enter your reset code" variant="outlined"></v-text-field>

                <p class="mt-1 mb-6" style="text-align: center">Enter a new password:</p>

                <v-text-field v-model="forgot_password.new_password" class="mb-2" :rules="[requiredRule]" label="New password" placeholder="Enter your new password" :type="make_password_visible ? 'text' : 'password'" @click:append-inner="make_password_visible = !make_password_visible" :append-inner-icon="make_password_visible ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>

                <v-text-field v-model="forgot_password.confirm_new_password" class="mb-2" :rules="[requiredRule]" label="Confirm new password" placeholder="Retype your new password" :type="make_password_visible ? 'text' : 'password'" @click:append-inner="make_password_visible = !make_password_visible" :append-inner-icon="make_password_visible ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>
            </v-form>

            <v-alert class="mb-4">
                Password must have at least:
                <ul>
                    <li>1 uppercase letter</li>
                    <li>1 lowercase letter</li>
                    <li>1 number</li>
                    <li>1 special character</li>
                    <li>8 characters in length</li>
                </ul>
            </v-alert>

            <v-alert type="error" v-if="!_isEmpty(error_message)" class="mb-4" icon="mdi-alert-circle">
                <span>{{ error_message }}</span>
            </v-alert>

            <v-btn color="var(--vt-c-orange)" size="large" variant="elevated" block class="text-white" @click="onForgotPasswordResetCodeSubmit()" :disabled="forgot_password_enter_code_form_valid === false || _isEmpty(forgot_password.verification_code) || _isEmpty(forgot_password.new_password) || _isEmpty(forgot_password.confirm_new_password) || error_message === passwords_do_not_match_text"> Change Password </v-btn>
        </v-card>
    </div>

    <div class="forgot_password" v-else-if="current_step === 'successfully_reset_password'">
        <v-card class="mx-auto px-6 py-8" elevation="2">
            <p class="mb-3 mx-10" style="font-weight: 500; font-size: 1.2rem; text-align: center">Password Changed Successfully</p>
            <p class="mt-3 mb-5 mx-5" style="text-align: center">You can now login with your new password.</p>

            <v-btn color="var(--vt-c-orange)" size="large" variant="elevated" block class="text-white" @click="current_step = 'login'"> Login </v-btn>
        </v-card>
    </div>
</template>

<style scoped>
.v-card {
    max-width: 344px;
    min-width: 344px;
}

.forgot_password_link,
.register_link {
    text-decoration: underline;
    color: var(--vt-c-orange);
    display: block;
    cursor: pointer;
    font-weight: 400;
}

.v-alert ul li {
    margin-left: 30px;
}

/* Override global style */
:deep(input[type="text"].v-field__input) {
    padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
    padding-top: var(--v-field-input-padding-top);
    padding-bottom: var(--v-field-input-padding-bottom);
}

@media (max-width: 360px) {
    .v-card {
        min-width: 95vw;
        max-width: 95vw;
    }
}
</style>
